import { useEffect } from 'react';
import type { RefObject } from 'react';

// Trailing debounce after the last observed size change — the panes animate
// width/flex-basis for 0.22s, and re-firing per animation frame would make
// Fortune-Sheet re-layout its canvases ~60× per transition.
const SETTLE_MS = 120;

// The element Fortune-Sheet sizes its canvases from. It fills the space left by
// the toolbar, formula bar and sheet-tab row, so it changes height whenever one
// of those does — without the wrapper itself changing size.
export const GRID_AREA_SELECTOR = '.fortune-sheet-canvas-placeholder';

/**
 * Fortune-Sheet sizes its canvases from the container ONLY on window `resize`
 * (it installs no ResizeObserver of its own) — so a layout-driven size change
 * leaves the grid at its stale size until the user happens to resize the
 * window. Two things change it: the wrapper itself (the history/comments panes
 * animating open or closed), and the grid area inside it (the toolbar wrapping
 * onto a second row, or back to one, as the selected cell's format label
 * changes width). The second case left the canvas and the horizontal scrollbar
 * a toolbar-row out of place — drawn over the sheet-tab row when the toolbar
 * grew. Observe both and dispatch a window resize once the size settles.
 *
 * `enabled` must be the wrapper's render gate (same contract as
 * useSheetTabWheelScroll): the wrapper div doesn't exist while the sheet is
 * loading, and a ref dep alone never re-fires this effect when it mounts.
 * `remountKey` re-finds the grid area after the Workbook remounts.
 */
export const useWorkbookResizeSync = (wrapperRef: RefObject<HTMLElement>, enabled: boolean, remountKey?: string | number): void => {
    useEffect(() => {
        if (!enabled) return undefined;
        const el = wrapperRef.current;
        if (!el || typeof ResizeObserver === 'undefined') return undefined;
        let timer: number | null = null;
        const sizes = new Map<Element, { w: number; h: number }>();
        const observer = new ResizeObserver((entries) => {
            let changed = false;
            entries.forEach(({ target }) => {
                const next = { w: (target as HTMLElement).clientWidth, h: (target as HTMLElement).clientHeight };
                const last = sizes.get(target);
                sizes.set(target, next);
                // no `last` = this target's initial observe callback
                if (last && (last.w !== next.w || last.h !== next.h)) changed = true;
            });
            if (!changed) return;
            if (timer) window.clearTimeout(timer);
            timer = window.setTimeout(() => window.dispatchEvent(new Event('resize')), SETTLE_MS);
        });
        observer.observe(el);
        // The Workbook mounts its grid a commit after the wrapper appears (it
        // renders the sheet only once its own state is seeded), so the grid
        // area is usually not in the DOM yet: wait for it.
        let finder: MutationObserver | null = null;
        const watchGridArea = (): boolean => {
            const gridArea = el.querySelector(GRID_AREA_SELECTOR);
            if (!gridArea) return false;
            observer.observe(gridArea);
            return true;
        };
        if (!watchGridArea() && typeof MutationObserver !== 'undefined') {
            finder = new MutationObserver(() => {
                if (watchGridArea()) finder?.disconnect();
            });
            finder.observe(el, { childList: true, subtree: true });
        }
        return () => {
            observer.disconnect();
            finder?.disconnect();
            if (timer) window.clearTimeout(timer);
        };
    }, [wrapperRef, enabled, remountKey]);
};
